import { useState } from "react";
import { motion } from "framer-motion";
import { CalendarCheck, CheckCircle2, Loader2, Mail, Clock, Banknote, CreditCard, ShieldCheck, Receipt } from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const SERVICES = [
  "Chronic Pain Management",
  "Pelvic Health Physiotherapy",
  "Post-Operative Rehabilitation",
  "Sports Rehabilitation & Performance",
  "Manual Therapy",
  "Electrotherapy & Physical Modalities",
  "Exercise Therapy & Functional Rehabilitation",
];

const TIME_SLOTS = ["08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00"];

const EMPTY = { name: "", email: "", phone: "", service: "", date: "", time: "", notes: "" };

export const Booking = () => {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });
  const today = new Date().toISOString().split("T")[0];

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service || !form.date || !form.time) {
      toast.error("Please choose a service, date and time");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/bookings", form);
      setConfirmed(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="book" data-testid="booking-section" className="relative bg-navy py-28 lg:py-40 overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <img
          src="https://images.unsplash.com/photo-1762625570087-6d98fca29531?auto=format&fit=crop&w=2000&q=80"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 mb-16 grid grid-cols-2 lg:grid-cols-4 gap-4"
        data-testid="payment-strip"
      >
        {[
          { icon: Banknote, title: "R450 per consultation", sub: "Transparent fee structure" },
          { icon: ShieldCheck, title: "Medical aid accepted", sub: "Differences explained upfront" },
          { icon: CreditCard, title: "Cash & card payments", sub: "Payment arrangements considered" },
          { icon: Receipt, title: "Itemised invoices", sub: "Provided after every session" },
        ].map((f, i) => {
          const Icon = f.icon;
          return (
            <div key={i} data-testid={`payment-chip-${i}`} className="rounded-lg border border-white/15 bg-white/10 backdrop-blur-md p-5 lg:p-6 flex flex-col gap-3">
              <Icon size={22} className="text-electric" />
              <div>
                <p className="text-white text-sm font-bold">{f.title}</p>
                <p className="text-white/55 text-xs mt-1">{f.sub}</p>
              </div>
            </div>
          );
        })}
      </motion.div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-14 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-5"
        >
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-electric mb-6">Appointments</p>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-white leading-[0.95] mb-8">
            Book your<br />first session.
          </h2>
          <p className="text-white/70 text-base lg:text-lg leading-relaxed mb-10 max-w-md">
            Your first appointment includes an in-depth assessment and a treatment plan built
            around your goals. Consultations are R450 — you'll get instant confirmation by email.
          </p>
          <ul className="space-y-4 text-white/70 text-sm">
            <li className="flex items-center gap-3"><Clock size={16} className="text-electric" /> Mon – Fri, 08:00 – 17:00</li>
            <li className="flex items-center gap-3"><Mail size={16} className="text-electric" /> Confirmation emailed instantly</li>
            <li className="flex items-center gap-3"><CalendarCheck size={16} className="text-electric" /> 24-hour cancellation policy</li>
          </ul>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ delay: 0.15, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7"
        >
          {confirmed ? (
            <div className="bg-white rounded-lg p-10 lg:p-14 text-center" data-testid="booking-confirmation">
              <CheckCircle2 size={56} className="text-electric mx-auto mb-6" />
              <h3 className="font-display font-extrabold text-3xl text-ink tracking-tight mb-4">You're booked in!</h3>
              <p className="text-neutral-600 mb-8">
                {confirmed.booking.name}, your <strong>{confirmed.booking.service}</strong> appointment is
                confirmed for <strong>{confirmed.booking.date}</strong> at <strong>{confirmed.booking.time}</strong>.
              </p>
              <p className="text-sm text-neutral-500 mb-10" data-testid="booking-email-status">
                {confirmed.email_sent
                  ? `A confirmation email is on its way to ${confirmed.booking.email}.`
                  : "Booking saved — email confirmation could not be sent, but you're on our schedule."}
              </p>
              <Button
                data-testid="book-another-button"
                onClick={() => { setConfirmed(null); setForm(EMPTY); }}
                className="rounded-full bg-navy hover:bg-electric text-white text-xs font-bold tracking-[0.2em] uppercase px-10 h-12 transition-colors duration-300"
              >
                Book Another Appointment
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-white rounded-lg p-8 lg:p-12 shadow-[0_24px_64px_rgba(0,0,0,0.35)]" data-testid="booking-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold tracking-[0.15em] uppercase text-neutral-500 mb-2">Full name</label>
                  <Input data-testid="booking-name-input" value={form.name} onChange={set("name")} placeholder="Your full name" required className="h-12" />
                </div>
                <div>
                  <label className="block text-xs font-semibold tracking-[0.15em] uppercase text-neutral-500 mb-2">Email</label>
                  <Input data-testid="booking-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" required className="h-12" />
                </div>
                <div>
                  <label className="block text-xs font-semibold tracking-[0.15em] uppercase text-neutral-500 mb-2">Phone</label>
                  <Input data-testid="booking-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="021 000 0000" required className="h-12" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold tracking-[0.15em] uppercase text-neutral-500 mb-2">Service</label>
                  <Select value={form.service} onValueChange={set("service")}>
                    <SelectTrigger data-testid="booking-service-select" className="h-12">
                      <SelectValue placeholder="Choose a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`service-option-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-xs font-semibold tracking-[0.15em] uppercase text-neutral-500 mb-2">Preferred date</label>
                  <Input data-testid="booking-date-input" type="date" min={today} value={form.date} onChange={set("date")} required className="h-12" />
                </div>
                <div>
                  <label className="block text-xs font-semibold tracking-[0.15em] uppercase text-neutral-500 mb-2">Preferred time</label>
                  <Select value={form.time} onValueChange={set("time")}>
                    <SelectTrigger data-testid="booking-time-select" className="h-12">
                      <SelectValue placeholder="Choose a time" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`time-option-${t.replace(":", "")}`}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs font-semibold tracking-[0.15em] uppercase text-neutral-500 mb-2">Notes <span className="text-neutral-400 normal-case tracking-normal">(optional)</span></label>
                  <Textarea data-testid="booking-notes-input" value={form.notes} onChange={set("notes")} placeholder="Tell us briefly about your injury or condition..." rows={3} />
                </div>
              </div>
              <Button
                type="submit"
                data-testid="booking-submit-button"
                disabled={submitting}
                className="mt-8 w-full h-14 rounded-full bg-electric hover:bg-navy text-white text-xs font-bold tracking-[0.25em] uppercase transition-colors duration-300"
              >
                {submitting ? <Loader2 className="animate-spin" size={20} /> : "Confirm Booking — R450"}
              </Button>
              <p className="mt-4 text-center text-xs text-neutral-400">
                Medical aid accepted. Itemised invoice provided after your session.
              </p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
